import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMinPaidTicketAmount, isValidTicketPrice } from '@boletra/shared';
import { ApiClientError } from '../../lib/api.js';
import {
  useCreateTicketType,
  useDeleteTicketType,
  useUpdateTicketType,
  type CreateTicketTypePayload,
  type DashboardTicketType,
  type Locale,
  type TicketKind,
  type UpdateTicketTypePayload,
} from '../../lib/tickets.js';

interface Props {
  eventId: string;
  currency: string;
  defaultLocale: Locale;
  editLocale: Locale;
  ticketTypes: DashboardTicketType[];
  /**
   * Lo usa el ManagementBar de la página: cuando hay 0 tipos visibles y
   * el evento está en draft, mostramos el warning de "necesitás un tipo
   * antes de publicar". Esto solo lo controla el parent.
   */
}

const KIND_VALUES: TicketKind[] = ['entry', 'combo', 'seat', 'consumption'];

/**
 * Convierte `priceCents` a string formateado en la moneda principal del
 * evento, pero como el input recibe el monto en unidades enteras de la
 * moneda (no en centavos), lo dividimos por 100. Si en algún momento se
 * agregan monedas sin minor units (ej. JPY) esta función queda como punto
 * único de cambio.
 */
function centsToMajor(cents: number): string {
  return (cents / 100).toFixed(2);
}

function majorToCents(major: string): number {
  const num = Number(major);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.round(num * 100);
}

export function TicketTypesManager({
  eventId,
  currency,
  defaultLocale,
  editLocale,
  ticketTypes,
}: Props) {
  const { t, i18n } = useTranslation('dashboard');
  const fmt = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'currency', currency }),
    [i18n.language, currency],
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const sorted = useMemo(
    () => [...ticketTypes].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)),
    [ticketTypes],
  );

  return (
    <section className="border-t border-neutral-900 pt-8 mt-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold">{t('tickets.section_title')}</h2>
          <p className="text-sm text-neutral-400 mt-1 max-w-prose">
            {t('tickets.section_subtitle')}
          </p>
        </div>
        {!creating && editingId === null && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn-primary w-full sm:w-auto"
          >
            + {t('tickets.add_button')}
          </button>
        )}
      </div>

      <p className="text-xs text-neutral-500 mb-4">{t('tickets.currency_hint', { currency })}</p>

      {creating && (
        <TicketForm
          eventId={eventId}
          mode="create"
          editLocale={editLocale}
          defaultLocale={defaultLocale}
          fmt={fmt}
          currency={currency}
          onDone={() => setCreating(false)}
        />
      )}

      {sorted.length === 0 && !creating ? (
        <div className="card text-center">
          <h3 className="font-semibold text-lg mb-2">{t('tickets.empty_title')}</h3>
          <p className="text-neutral-400 text-sm mb-4">{t('tickets.empty_body')}</p>
          <button type="button" onClick={() => setCreating(true)} className="btn-primary">
            + {t('tickets.add_button')}
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {sorted.map((tt) =>
            editingId === tt.id ? (
              <li key={tt.id}>
                <TicketForm
                  eventId={eventId}
                  mode="edit"
                  initial={tt}
                  editLocale={editLocale}
                  defaultLocale={defaultLocale}
                  fmt={fmt}
                  currency={currency}
                  onDone={() => setEditingId(null)}
                />
              </li>
            ) : (
              <li key={tt.id}>
                <TicketRow
                  ticket={tt}
                  eventId={eventId}
                  editLocale={editLocale}
                  defaultLocale={defaultLocale}
                  fmt={fmt}
                  onEdit={() => setEditingId(tt.id)}
                />
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}

function TicketRow({
  ticket,
  eventId,
  editLocale,
  defaultLocale,
  fmt,
  onEdit,
}: {
  ticket: DashboardTicketType;
  eventId: string;
  editLocale: Locale;
  defaultLocale: Locale;
  fmt: Intl.NumberFormat;
  onEdit: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const tr = ticket.translations[editLocale] ??
    ticket.translations[defaultLocale] ??
    Object.values(ticket.translations)[0] ?? { name: '', description: '' };
  const remaining = Math.max(0, ticket.capacity - ticket.sold);
  const isSoldOut = ticket.capacity > 0 && remaining === 0;
  const progress =
    ticket.capacity > 0 ? Math.min(100, Math.round((ticket.sold / ticket.capacity) * 100)) : 0;

  const update = useUpdateTicketType(eventId);
  const remove = useDeleteTicketType(eventId);

  const handleToggleHidden = () => {
    const payload: UpdateTicketTypePayload = { hidden: !ticket.hidden };
    update.mutate({ id: ticket.id, payload });
  };

  const handleDelete = () => {
    const msg =
      ticket.sold > 0 ? t('tickets.delete_with_sales_warning') : t('tickets.delete_confirm');
    if (!window.confirm(msg)) return;
    remove.mutate(ticket.id);
  };

  return (
    <div className={['card flex flex-col gap-4', ticket.hidden ? 'opacity-70' : ''].join(' ')}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-lg leading-tight break-words">
              {tr.name || t('ticket_form.untitled')}
            </h3>
            {isSoldOut && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">
                {t('tickets.sold_out_badge')}
              </span>
            )}
            {ticket.hidden && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-neutral-700/40 text-neutral-300 border border-neutral-700">
                {t('tickets.hidden_badge')}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider text-neutral-500">
              · {t(`tickets.kind.${ticket.kind}`)}
            </span>
          </div>
          {tr.description && (
            <p className="text-sm text-neutral-400 mt-1 line-clamp-2 break-words">
              {tr.description}
            </p>
          )}
        </div>
        <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 sm:gap-1 sm:text-right shrink-0">
          <p className="text-2xl font-bold tabular-nums">
            {ticket.priceCents === 0
              ? t('tickets.free_price')
              : fmt.format(ticket.priceCents / 100)}
          </p>
          {ticket.maxPerOrder != null && (
            <p className="text-xs text-neutral-500 whitespace-nowrap">
              {t('tickets.max_per_order_value', { count: ticket.maxPerOrder })}
            </p>
          )}
        </div>
      </div>

      <div>
        <div className="flex justify-between text-xs text-neutral-500 mb-1.5 tabular-nums">
          <span>
            {t('tickets.sold_label')}: {ticket.sold.toLocaleString()} /{' '}
            {ticket.capacity.toLocaleString()}
          </span>
          <span>
            {t('tickets.remaining_label')}: {remaining.toLocaleString()}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-neutral-900 overflow-hidden">
          <div
            className={[
              'h-full transition-all',
              isSoldOut ? 'bg-red-500/60' : 'bg-[var(--color-accent)]',
            ].join(' ')}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 -mb-1">
        <button
          type="button"
          onClick={onEdit}
          className="text-sm px-3 py-2 rounded-lg border border-neutral-800 hover:border-neutral-600 transition flex-1 sm:flex-none min-w-0"
        >
          {t('tickets.edit_button')}
        </button>
        <button
          type="button"
          onClick={handleToggleHidden}
          disabled={update.isPending}
          className="text-sm px-3 py-2 rounded-lg border border-neutral-800 hover:border-neutral-600 transition flex-1 sm:flex-none min-w-0 disabled:opacity-50"
        >
          {ticket.hidden ? t('tickets.show_button') : t('tickets.hide_button')}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={remove.isPending}
          className="text-sm px-3 py-2 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 transition flex-1 sm:flex-none min-w-0 disabled:opacity-50"
        >
          {t('tickets.delete_button')}
        </button>
      </div>
      {update.isError && (
        <p className="text-red-400 text-xs">{(update.error as ApiClientError).message}</p>
      )}
      {remove.isError && (
        <p className="text-red-400 text-xs">{(remove.error as ApiClientError).message}</p>
      )}
    </div>
  );
}

interface FormState {
  kind: TicketKind;
  name: string;
  description: string;
  priceMajor: string;
  capacity: string;
  /** Vacío = sin límite por compra. */
  maxPerOrder: string;
  hidden: boolean;
}

function emptyForm(): FormState {
  return {
    kind: 'entry',
    name: '',
    description: '',
    priceMajor: '0',
    capacity: '100',
    maxPerOrder: '',
    hidden: false,
  };
}

function parseMaxPerOrder(raw: string): number | null {
  const n = Math.floor(Number(raw));
  if (raw.trim() === '' || !Number.isFinite(n) || n < 1) return null;
  return Math.min(100, n);
}

function TicketForm({
  eventId,
  mode,
  initial,
  editLocale,
  defaultLocale,
  fmt,
  currency,
  onDone,
}: {
  eventId: string;
  mode: 'create' | 'edit';
  initial?: DashboardTicketType;
  editLocale: Locale;
  defaultLocale: Locale;
  fmt: Intl.NumberFormat;
  currency: string;
  onDone: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const create = useCreateTicketType(eventId);
  const update = useUpdateTicketType(eventId);

  const [state, setState] = useState<FormState>(() => {
    if (!initial) return emptyForm();
    const tr = initial.translations[editLocale] ??
      initial.translations[defaultLocale] ??
      Object.values(initial.translations)[0] ?? { name: '', description: '' };
    return {
      kind: initial.kind,
      name: tr.name ?? '',
      description: tr.description ?? '',
      priceMajor: centsToMajor(initial.priceCents),
      capacity: String(initial.capacity),
      maxPerOrder: initial.maxPerOrder != null ? String(initial.maxPerOrder) : '',
      hidden: initial.hidden,
    };
  });

  // Cuando cambia la locale activa mientras editamos, refrescamos las
  // strings traducidas — sin esto el usuario edita ES pero ve EN al saltar.
  useEffect(() => {
    if (!initial) return;
    const tr = initial.translations[editLocale] ??
      initial.translations[defaultLocale] ??
      Object.values(initial.translations)[0] ?? { name: '', description: '' };
    setState((s) => ({ ...s, name: tr.name ?? '', description: tr.description ?? '' }));
  }, [editLocale, initial, defaultLocale]);

  const priceCents = majorToCents(state.priceMajor);
  const capacity = Math.max(0, Math.floor(Number(state.capacity) || 0));
  const sold = initial?.sold ?? 0;
  const capacityBelowSold = mode === 'edit' && capacity < sold;
  const priceBelowMin = !isValidTicketPrice(priceCents, currency);
  const submitting = create.isPending || update.isPending;
  const canSubmit =
    state.name.trim().length >= 1 &&
    state.capacity.length > 0 &&
    !capacityBelowSold &&
    !priceBelowMin &&
    !submitting;

  // Mín en mayor unit (e.g. "20.00" para MXN, "150" para JPY). Lo usamos en
  // el helper text y en el error inline cuando el promoter está abajo del
  // piso. La conversión a major es la misma división por 100 que el resto
  // del form — si más adelante se corrigen los zero-decimal currencies
  // (JPY/CLP) hay que tocar centsToMajor también.
  const minPaidMajor = useMemo(() => {
    const minMinor = getMinPaidTicketAmount(currency);
    return fmt.format(minMinor / 100);
  }, [currency, fmt]);

  const error = (create.error ?? update.error) as ApiClientError | null;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const translation = { name: state.name.trim(), description: state.description.trim() };

    if (mode === 'create') {
      const translations: Record<string, { name: string; description: string }> = {
        [editLocale]: translation,
      };
      // Asegurar que la defaultLocale tenga algo aunque el usuario esté
      // editando otra — si no, el público en la default ve un nombre vacío.
      if (editLocale !== defaultLocale && !translations[defaultLocale]) {
        translations[defaultLocale] = translation;
      }
      const payload: CreateTicketTypePayload = {
        kind: state.kind,
        priceCents,
        capacity,
        maxPerOrder: parseMaxPerOrder(state.maxPerOrder),
        hidden: state.hidden,
        translations,
      };
      create.mutate(payload, { onSuccess: onDone });
      return;
    }

    if (!initial) return;
    const mergedTranslations = {
      ...initial.translations,
      [editLocale]: translation,
    };
    const payload: UpdateTicketTypePayload = {
      priceCents,
      capacity,
      maxPerOrder: parseMaxPerOrder(state.maxPerOrder),
      hidden: state.hidden,
      translations: mergedTranslations,
    };
    update.mutate({ id: initial.id, payload }, { onSuccess: onDone });
  };

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-lg">
          {mode === 'create' ? t('tickets.create_title') : t('tickets.edit_title')}
        </h3>
        {mode === 'edit' && initial && initial.sold > 0 && (
          <span className="text-xs text-neutral-500">
            {t('tickets.sold_label')}: {initial.sold} / {initial.capacity}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label={t('tickets.kind_label')}>
          <select
            value={state.kind}
            onChange={(e) => setState({ ...state, kind: e.target.value as TicketKind })}
            className="input"
            disabled={mode === 'edit'}
          >
            {KIND_VALUES.map((k) => (
              <option key={k} value={k}>
                {t(`tickets.kind.${k}`)}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label={t('tickets.name_label')}>
          <input
            type="text"
            value={state.name}
            onChange={(e) => setState({ ...state, name: e.target.value })}
            placeholder={t('tickets.name_placeholder')}
            maxLength={120}
            className="input"
            autoFocus
          />
        </FormField>
      </div>

      <FormField label={t('tickets.description_label')}>
        <textarea
          value={state.description}
          onChange={(e) => setState({ ...state, description: e.target.value })}
          placeholder={t('tickets.description_placeholder')}
          maxLength={2000}
          rows={3}
          className="input resize-y"
        />
      </FormField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField
          label={t('tickets.price_label')}
          hint={
            priceBelowMin && priceCents > 0
              ? t('tickets.price_below_min_error', { min: minPaidMajor })
              : t('tickets.price_min_hint', { min: minPaidMajor })
          }
        >
          <div className="relative">
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={state.priceMajor}
              onChange={(e) => setState({ ...state, priceMajor: e.target.value })}
              className="input pr-16"
              aria-invalid={priceBelowMin && priceCents > 0}
            />
            <span className="absolute inset-y-0 right-3 flex items-center text-xs text-neutral-500 pointer-events-none">
              {fmt.formatToParts(0).find((p) => p.type === 'currency')?.value ?? ''}
            </span>
          </div>
        </FormField>

        <FormField
          label={t('tickets.capacity_label')}
          hint={
            capacityBelowSold
              ? t('tickets.capacity_below_sold_error', { sold })
              : t('tickets.capacity_help')
          }
        >
          <input
            type="number"
            inputMode="numeric"
            min={mode === 'edit' ? sold : 0}
            step={1}
            value={state.capacity}
            onChange={(e) => setState({ ...state, capacity: e.target.value })}
            className={['input', capacityBelowSold ? 'border-red-500/50' : ''].join(' ')}
          />
        </FormField>

        <FormField label={t('tickets.max_per_order_label')} hint={t('tickets.max_per_order_help')}>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={100}
            step={1}
            value={state.maxPerOrder}
            onChange={(e) => setState({ ...state, maxPerOrder: e.target.value })}
            className="input"
          />
        </FormField>
      </div>

      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={state.hidden}
          onChange={(e) => setState({ ...state, hidden: e.target.checked })}
          className="mt-1 w-4 h-4 rounded border-neutral-700 bg-neutral-900 text-[var(--color-accent)] focus:ring-[var(--color-accent)]/30"
        />
        <span className="text-sm">
          <span className="block text-neutral-100">{t('tickets.hidden_label')}</span>
          <span className="block text-neutral-500 text-xs mt-0.5">{t('tickets.hidden_help')}</span>
        </span>
      </label>

      {error && <p className="text-red-400 text-sm">{error.message}</p>}

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-2">
        <button
          type="button"
          onClick={onDone}
          disabled={submitting}
          className="btn-secondary w-full sm:w-auto"
        >
          {t('tickets.cancel_button')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="btn-primary w-full sm:flex-1"
        >
          {submitting ? '…' : t('tickets.save_button')}
        </button>
      </div>
    </div>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm text-neutral-400">{label}</span>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-xs text-neutral-500 mt-1.5">{hint}</p>}
    </label>
  );
}
